'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { X, Check, ShoppingCart, CakeSlice } from 'lucide-react'
import toast from 'react-hot-toast'
import { useCartStore, type CartCustomization } from '@/store/cartStore'
import QuantityStepper from '@/components/ui/QuantityStepper'
import BulkOrderNotice from '@/components/ui/BulkOrderNotice'
import {
  DEFAULT_BULK_MAX_ONLINE_QTY, BAKERY_PHONE_HREF, BAKERY_PHONE_DISPLAY,
  slicingCustomization, withSlicedKey, DEFAULT_SLICING_PRICE,
} from '@/lib/bakery'

/* ─── Types ─────────────────────────────────────────────────────────────── */
export interface QuickAddOption { id: string; name: string; priceAddon: number; isDefault: boolean }
export interface QuickAddGroup  { id: string; name: string; isRequired: boolean; options: QuickAddOption[] }
export interface QuickAddProduct {
  id:        string
  name:      string
  slug:      string
  price:     number
  salePrice?: number | null
  category?: { name: string } | null
  optionGroups?: QuickAddGroup[]
  bulkMaxOnlineQty?: number | null
  orderMinQty?: number | null
  outOfSeasonOrder?: boolean
  minLeadDays?: number | null
  canBeSliced?: boolean
  slicingPrice?: number
}

function formatPrice(value: number): string {
  return `€${value.toFixed(2).replace('.', ',')}`
}

/**
 * Compact "add to cart" chooser: option pickers (Half/Heel brood, etc.) + a
 * quantity stepper + live total. Shared by the shop grid (ProductCard) and the
 * cart's upsell row so the choose-your-variant flow is identical everywhere.
 *
 * The parent owns open/close state and wraps this in a portal + AnimatePresence.
 */
export default function QuickAddModal({
  product,
  mainImage,
  activePrice,
  isOnSale,
  onClose,
}: {
  product: QuickAddProduct
  mainImage: string
  activePrice: number          // effective per-unit base price (sale or regular)
  isOnSale: boolean
  onClose: () => void
}) {
  const addItem = useCartStore((s) => s.addItem)
  const groups = product.optionGroups ?? []
  const maxOnlineQty = product.bulkMaxOnlineQty ?? DEFAULT_BULK_MAX_ONLINE_QTY
  const minQty       = Math.max(1, product.orderMinQty ?? 1)

  /* Default-select the isDefault option of each group (mirrors product page). */
  const [selectedOpts, setSelectedOpts] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {}
    groups.forEach((g) => {
      const def = g.options.find((o) => o.isDefault) ?? g.options[0]
      if (def) init[g.id] = def.id
    })
    return init
  })
  const [quantity, setQuantity] = useState(minQty)
  /* "Laten snijden" — alleen bij taarten, vlaaien, cake en slofjes. De prijs
     staat in Beheer (0 = gratis). */
  const [sliced, setSliced] = useState(false)
  const slicingPrice = product.slicingPrice ?? DEFAULT_SLICING_PRICE

  /* Lock body scroll + close on Escape */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  const totalAddons = groups.reduce((sum, g) => {
    const opt = g.options.find((o) => o.id === selectedOpts[g.id])
    return sum + (opt?.priceAddon ?? 0)
  }, 0)
  const unitPrice = activePrice + totalAddons
  const total = unitPrice * quantity

  const handleConfirm = () => {
    for (const g of groups) {
      if (g.isRequired && !selectedOpts[g.id]) {
        toast.error(`Kies een optie voor "${g.name}"`)
        return
      }
    }
    const customizations: CartCustomization[] = groups.flatMap((g) => {
      const opt = g.options.find((o) => o.id === selectedOpts[g.id])
      if (!opt) return []
      return [{ groupId: g.id, groupName: g.name, optionId: opt.id, optionName: opt.name, priceAddon: opt.priceAddon }]
    })
    if (product.canBeSliced && sliced) customizations.push(slicingCustomization(slicingPrice))
    const sortedOptIds = Object.values(selectedOpts).sort().join(':')
    const baseKey = sortedOptIds ? `${product.id}:${sortedOptIds}` : product.id
    const cartKey = withSlicedKey(baseKey, !!product.canBeSliced && sliced)

    addItem({
      cartKey,
      productId: product.id,
      name:      product.name,
      price:     product.price,
      salePrice: product.salePrice ?? null,
      image:     mainImage,
      slug:      product.slug,
      customizations: customizations.length ? customizations : undefined,
      maxOnlineQty,
      minQty:      minQty > 1 ? minQty : undefined,
      minLeadDays: product.minLeadDays ?? undefined,
      canBeSliced:  product.canBeSliced || undefined,
      slicingPrice: product.canBeSliced ? slicingPrice : undefined,
    }, quantity)

    onClose()
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      className="fixed inset-0 z-[170] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(28,14,8,0.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      role="dialog"
      aria-modal="true"
      aria-label={`Toevoegen aan winkelwagen: ${product.name}`}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 12 }}
        transition={{ type: 'spring', damping: 28, stiffness: 340 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-sm bg-[#FDF6EC] shadow-2xl rounded-t-3xl sm:rounded-3xl overflow-hidden"
      >
        {/* Accent bar */}
        <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #8B4513, #D2691E, #DAA520)' }} />

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-[#8B4513] shadow-md hover:bg-white hover:rotate-90 transition-all duration-200"
          aria-label="Sluiten"
        >
          <X size={18} />
        </button>

        <div className="p-5">
          {/* Header: thumbnail + name */}
          <div className="flex items-center gap-3 mb-5 pr-8">
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#D2691E]/10 shrink-0">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={mainImage}
                alt={product.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={(e) => { (e.target as HTMLImageElement).src = '/images/placeholder-product.svg' }}
              />
            </div>
            <div className="min-w-0">
              {product.category && (
                <p className="text-[11px] text-[#D2691E] font-semibold uppercase tracking-wide">{product.category.name}</p>
              )}
              <h3
                className="font-playfair font-bold text-[#2C1810] leading-tight line-clamp-2"
                style={{ fontFamily: 'var(--font-playfair), Playfair Display, Georgia, serif' }}
              >
                {product.name}
              </h3>
            </div>
          </div>

          {/* Option groups */}
          {groups.map((group) => (
            <div key={group.id} className="mb-5">
              <p className="text-xs font-bold text-[#75665D] uppercase tracking-widest mb-2">{group.name}</p>
              <div className="flex flex-wrap gap-2">
                {group.options.map((opt) => {
                  const selected = selectedOpts[group.id] === opt.id
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setSelectedOpts((prev) => ({ ...prev, [group.id]: opt.id }))}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                        selected
                          ? 'bg-[#8B4513] text-white border-[#8B4513]'
                          : 'bg-white text-[#2C1810]/75 border-[#D2691E]/25 hover:border-[#8B4513]/40'
                      }`}
                    >
                      {selected && <Check size={13} />}
                      {opt.name}
                      {opt.priceAddon !== 0 && (
                        <span className={selected ? 'text-white/80' : 'text-[#8B4513]'}>
                          {opt.priceAddon > 0 ? '+' : '−'}{formatPrice(Math.abs(opt.priceAddon))}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}

          {/* Minimum-quantity notice (seizoen rule) */}
          {minQty > 1 && (
            <div className="mb-4 rounded-xl px-3 py-2.5 text-xs" style={{ background: '#FEF3E8', border: '1.5px solid #F4A460', color: '#2C1810' }}>
              {product.outOfSeasonOrder
                ? <>
                    Buiten het seizoen alleen te bestellen vanaf <strong>{minQty} stuks</strong>.
                    {' '}Speciaal verzoek? Bel ons op{' '}
                    <a href={`tel:${BAKERY_PHONE_HREF}`} className="font-bold underline" style={{ color: '#8B4513' }}>
                      {BAKERY_PHONE_DISPLAY}
                    </a>.
                  </>
                : <>Minimale afname: <strong>{minQty} stuks</strong>.</>}
            </div>
          )}

          {/* Laten snijden — gratis, alleen bij producten waar het aan staat */}
          {product.canBeSliced && (
            <label
              className="mb-4 flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 transition-colors"
              style={{
                background: sliced ? '#F0FDF4' : '#FEF3E8',
                border: sliced ? '1.5px solid #86EFAC' : '1.5px solid #F4A460',
              }}
            >
              <input
                type="checkbox"
                checked={sliced}
                onChange={(e) => setSliced(e.target.checked)}
                className="h-4 w-4 accent-[#16A34A]"
              />
              <CakeSlice size={15} className={sliced ? 'text-green-600' : 'text-[#8B4513]'} />
              <span className="text-xs" style={{ color: sliced ? '#166534' : '#2C1810' }}>
                <strong>Laten snijden</strong> - wij snijden hem voor u
                {slicingPrice > 0 ? ` (+${formatPrice(slicingPrice)})` : ' (gratis)'}
              </span>
            </label>
          )}

          {/* Quantity stepper */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-bold text-[#75665D] uppercase tracking-widest">Aantal</p>
            <QuantityStepper
              value={quantity}
              onChange={setQuantity}
              min={minQty}
              max={maxOnlineQty}
            />
          </div>

          {/* "Max online" notice */}
          {quantity >= maxOnlineQty && (
            <div className="mb-5">
              <BulkOrderNotice
                quantity={quantity}
                maxOnlineQty={maxOnlineQty}
                size="sm"
              />
            </div>
          )}

          {/* Confirm */}
          <button
            onClick={handleConfirm}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold transition-colors"
            style={{ background: '#8B4513', color: '#FDF6EC' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#D2691E')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#8B4513')}
          >
            <ShoppingCart size={18} />
            In winkelwagen
            <span className={isOnSale ? 'text-[#FFE2C2]' : undefined}>· {formatPrice(total)}</span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}
